import {
    AccountOverrideEntry,
    CategoryBackup,
    TransactionBase,
    TransactionStatus,
    TransactionType,
} from "../../types/transaction";
import { TaxQuestionKey } from "../../taxSavingRules";
import { RequiredAction } from "../../requiredActions";
import { FIELD_UPDATE_SOURCE } from "../../constants";
import { CounterpartyVerifiedScope } from "../../constants/counterparty";
import { assertRelationLoaded } from "../../helpers/orm";

// Base scalar transaction response. `accountOverrides` must be hydrated by
// backend response builders; returning [] for an unloaded field would falsely
// imply there are no overrides.
export class TransactionBaseDto {
    id: number;
    amount: number;
    originalAmount: number;
    foreignCurrencyAmount: number | null;
    foreignCurrency: string | null;
    /** @deprecated Use `dateOnly` for the accounting calendar day. Legacy UTC timestamp; persist on writes only. */
    date: string;
    dateOnly: string | null;
    type: TransactionType;
    status: TransactionStatus;
    bankDescription: string;
    normalizedBankDescription: string | null;
    normalizedDescriptionId: string | null;
    displayDescription: string | null;
    categoryId: string;
    financialAccountId: number;
    businessCategoryId: string | null;
    personalCategoryId: string | null;
    categoryBackup: CategoryBackup | null;
    memo: string | null;
    amountSaved: number | null;
    potentialAmountSaved: number | null;
    requiredActions: RequiredAction[];

    taxQuestions: TaxQuestionKey[];
    deductionRate: number;
    requirementsSnoozed: boolean;
    isIgnored: boolean;
    isBusiness: boolean;

    inReview: boolean;

    accountId: string | null;
    accrualAccountId: string | null;
    suggestedAccrualAccountId: string | null;
    accountOverrides: AccountOverrideEntry[];
    entityId: number;

    counterpartyId: string | null;
    customerId: string | null;
    billableExpenseInvoiceId: string | null;
    splitParentId: number | null;
    categorizationSource: FIELD_UPDATE_SOURCE | null;
    counterpartyIdSource: FIELD_UPDATE_SOURCE | null;
    categoryIdLastUpdatedAt?: Date | null;
    counterpartyIdLastUpdatedAt?: Date | null;
    counterpartyVerifiedScope?: CounterpartyVerifiedScope | null;
    counterpartyPredicted?: boolean;
    similarityVectorGenerated?: boolean;

    static deserialize(dto: TransactionBaseDto): TransactionBase {
        return {
            ...dto,
            date: new Date(dto.date),
        };
    }

    constructor(
        transaction: Omit<
            TransactionBase,
            | "auditLogs"
            | "financialAccount"
            | "taxQuestionAnswers"
            | "entity"
            | "transactionMatches"
        >,
    ) {
        this.id = transaction.id;
        this.amount = transaction.amount;
        this.originalAmount = transaction.originalAmount;
        this.foreignCurrencyAmount = transaction.foreignCurrencyAmount;
        this.foreignCurrency = transaction.foreignCurrency;
        this.date = transaction.date.toISOString();
        this.dateOnly = transaction.dateOnly ?? null;
        this.type = transaction.type;
        this.status = transaction.status;
        this.bankDescription = transaction.bankDescription;
        this.displayDescription = transaction.displayDescription;
        this.categoryId = transaction.categoryId;
        this.businessCategoryId = transaction.businessCategoryId;
        this.personalCategoryId = transaction.personalCategoryId;
        this.categoryBackup = transaction.categoryBackup;
        this.memo = transaction.memo;
        this.amountSaved = transaction.amountSaved;
        this.potentialAmountSaved = transaction.potentialAmountSaved;
        this.requiredActions = transaction.requiredActions;
        this.taxQuestions = transaction.taxQuestions;
        this.deductionRate = transaction.deductionRate;
        this.requirementsSnoozed = transaction.requirementsSnoozed;
        this.isIgnored = transaction.isIgnored;
        this.isBusiness = transaction.isBusiness;
        this.inReview = transaction.inReview;
        this.counterpartyId = transaction.counterpartyId;
        this.customerId = transaction.customerId;
        this.billableExpenseInvoiceId = transaction.billableExpenseInvoiceId;
        this.accountId = transaction.accountId;
        this.accrualAccountId = transaction.accrualAccountId;
        this.suggestedAccrualAccountId = transaction.suggestedAccrualAccountId;
        assertRelationLoaded(
            transaction,
            "accountOverrides",
            "Transaction accountOverrides must be hydrated before constructing TransactionBaseDto",
        );
        this.accountOverrides = transaction.accountOverrides;
        this.splitParentId = transaction.splitParentId ?? null;
        this.entityId = transaction.entityId;
        this.normalizedBankDescription = transaction.normalizedBankDescription;
        this.normalizedDescriptionId = transaction.normalizedDescriptionId;
        this.financialAccountId = transaction.financialAccountId;
        this.categorizationSource = transaction.categorizationSource;
        this.categoryIdLastUpdatedAt = transaction.categoryIdLastUpdatedAt;
        this.counterpartyIdSource = transaction.counterpartyIdSource;
        this.counterpartyIdLastUpdatedAt =
            transaction.counterpartyIdLastUpdatedAt;
        this.counterpartyVerifiedScope = transaction.counterpartyVerifiedScope;
        this.counterpartyPredicted = transaction.counterpartyPredicted;
        this.similarityVectorGenerated = transaction.similarityVectorGenerated;
    }
}
